import type { ReceiptWidthMm } from './types';

/** Half-up a `fractionDigits` (default 2) antes de mostrar en ticket/fiscal. */
function roundReceiptAmount(value: number, fractionDigits = 2): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** fractionDigits;
  return Math.round(value * factor + Number.EPSILON) / factor;
}

/**
 * Moneda es-AR en tickets térmicos y comprobantes impresos.
 * Paridad con `formatMoney` de negocio: enteros sin decimales, centavos con 2 fijos.
 */
export function formatReceiptMoney(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = roundReceiptAmount(Math.abs(n));
  const hasDecimals = abs % 1 !== 0;
  return `${sign}$${abs.toLocaleString('es-AR', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/** Columnas monospace aprox. para texto plano ESC/POS según ancho de rollo. */
export function receiptCharsPerLine(widthMm: ReceiptWidthMm): number {
  if (widthMm === 58) return 32;
  if (widthMm === 80) return 48;
  return 42;
}

/**
 * Columnas para HTML/pre vía browser (Windows → térmica).
 * Menor que ESC/POS: el driver suele imprimir menos chars que el preview;
 * no rellenar con espacios hasta el borde (recorta importes a la derecha).
 */
export function receiptPlainTextCols(widthMm: ReceiptWidthMm): number {
  if (widthMm === 58) return 22;
  if (widthMm === 80) return 30;
  return 28;
}

export function escapeReceiptHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatReceiptWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}
